import { Box, Stack } from "@mui/material";
import { I18N_NAMESPACES } from "@src/appV2/i18n/constants";
import { useTu } from "@src/appV2/i18n/useTu";

import { Text } from "../../../components/Text";
import { SettingToggle } from "../../components/SettingToggle";
import { type SettingsPermissions } from "../../types";

interface RateBillingSectionViewProps {
  isMapleIntegrationVisible: boolean;
  isRateNegotiationVisible: boolean;
  isUpdating: boolean;
  mapleEnabled: boolean;
  permissions: SettingsPermissions;
  rateNegotiationArticleUrl: string | undefined;
  rateNegotiationEnabled: boolean;
  onMapleIntegrationToggle: (checked: boolean) => void;
  onRateNegotiationToggle: (checked: boolean) => void;
}

export function RateBillingSectionView({
  isMapleIntegrationVisible,
  isRateNegotiationVisible,
  isUpdating,
  mapleEnabled,
  permissions,
  rateNegotiationArticleUrl,
  rateNegotiationEnabled,
  onMapleIntegrationToggle,
  onRateNegotiationToggle,
}: RateBillingSectionViewProps) {
  const { tu } = useTu(I18N_NAMESPACES.settings);
  const isFacilityOrCbhAdmin = permissions.isFacilityAdmin || permissions.isAdmin;

  const hasVisibleSettings = isRateNegotiationVisible || isMapleIntegrationVisible;
  const rateNegotiationSubtitle = rateNegotiationArticleUrl
    ? tu(
        "If this is turned on, professionals can propose a new pay rate, giving your workplace a fast way to fill more shifts. Learn more at the support center."
      )
    : tu(
        "If this is turned on, professionals can propose a new pay rate, giving your workplace a fast way to fill more shifts."
      );

  if (!hasVisibleSettings) {
    return (
      <Text color="text.secondary" variant="body2">
        {tu("No rate and billing settings are available for this workplace.")}
      </Text>
    );
  }

  return (
    <Stack>
      {isRateNegotiationVisible && (
        <SettingToggle
          checked={rateNegotiationEnabled}
          disabled={!isFacilityOrCbhAdmin || isUpdating}
          subtitle={rateNegotiationSubtitle}
          title={tu("Allow professionals to propose rates on shifts")}
          onChange={onRateNegotiationToggle}
        />
      )}

      {isRateNegotiationVisible && rateNegotiationArticleUrl && (
        <Box
          component="a"
          href={rateNegotiationArticleUrl}
          rel="noopener noreferrer"
          sx={(theme) => ({
            color: theme.palette.link?.base,
            fontSize: theme.typography.caption.fontSize,
            fontWeight: theme.typography.fontWeightMedium,
            marginTop: theme.spacing(-2),
            paddingBottom: theme.spacing(2),
            textDecoration: "none",
            "&:hover": { textDecoration: "underline" },
          })}
          target="_blank"
        >
          {tu("Learn more")}
        </Box>
      )}

      {isMapleIntegrationVisible && (
        <SettingToggle
          checked={mapleEnabled}
          disabled={!permissions.isAdmin || isUpdating}
          title={tu("Is this workplace integrated with Maple?")}
          onChange={onMapleIntegrationToggle}
        />
      )}
    </Stack>
  );
}
